<template>
  <div class="ios-menu-overlay" @click.self="closeDialog">
    <div class="ios-menu-container">
      <!-- 검색창 -->
      <div class="search-bar">
        <i class="ph ph-magnifying-glass"></i>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="검색"
          @input="handleSearch"
        />
        <Transition name="fade">
          <i 
            v-if="searchQuery" 
            class="ph ph-x-circle clear-btn" 
            @click="clearSearch"
          ></i>
        </Transition>
      </div>

      <!-- 폴더 그리드 -->
      <div class="folders-grid" :class="{ 'folder-open': openFolderIndex !== null }">
        <template v-for="(folder, index) in filteredMenus" :key="folder.title">
          <!-- 닫힌 폴더 -->
          <Transition name="folder-hide">
            <div 
              v-if="openFolderIndex === null || openFolderIndex === index"
              class="folder-wrapper"
              :class="{ 'is-open': openFolderIndex === index }"
            >
              <div 
                v-if="openFolderIndex !== index"
                class="folder"
                @click="openFolder(index)"
              >
                <div class="folder-preview">
                  <div 
                    v-for="(menu, i) in folder.menus.slice(0, 9)" 
                    :key="i"
                    class="preview-icon"
                  >
                    <i :class="menu.thumb"></i>
                  </div>
                </div>
                <span class="folder-title">{{ folder.title }}</span>
                <span class="folder-count">{{ folder.menus.length }}개</span>
              </div>

              <!-- 열린 폴더 -->
              <Transition name="expand" @after-enter="onFolderOpened">
                <div v-if="openFolderIndex === index" class="folder-expanded">
                  <div class="folder-header">
                    <button class="back-btn" @click="closeFolder">
                      <i class="ph ph-caret-left"></i>
                    </button>
                    <h2>{{ folder.title }}</h2>
                    <button class="close-btn" @click="closeDialog">
                      <i class="ph ph-x"></i>
                    </button>
                  </div>
                  <div class="folder-content">
                    <TransitionGroup name="menu-item" appear>
                      <div 
                        v-for="(menu, i) in folder.menus" 
                        :key="i"
                        class="menu-item"
                        :style="{ '--delay': i * 0.04 + 's' }"
                        @click="handleMenuClick(menu)"
                      >
                        <div class="menu-icon">
                          <i :class="menu.thumb"></i>
                        </div>
                        <span class="menu-title">{{ menu.title }}</span>
                      </div>
                    </TransitionGroup>
                  </div>
                </div>
              </Transition>
            </div>
          </Transition>
        </template>
      </div>

      <!-- 하단 닫기 버튼 -->
      <Transition name="fade">
        <button 
          v-if="openFolderIndex === null" 
          class="bottom-close-btn"
          @click="closeDialog"
        >
          <i class="ph ph-x"></i>
        </button>
      </Transition>
    </div>
  </div>
</template>

<script>
import { inject, reactive, ref, computed, onMounted, onUnmounted } from 'vue';

export default {
  emits: ['close'],
  setup(props, { emit }) {
    const injectedMenus = inject('mainMenus');
    const mainMenus = reactive(injectedMenus);
    
    const openFolderIndex = ref(null);
    const searchQuery = ref('');

    const filteredMenus = computed(() => {
      if (!searchQuery.value) return mainMenus;
      
      const query = searchQuery.value.toLowerCase();
      return mainMenus.map(folder => ({
        ...folder,
        menus: folder.menus.filter(menu => 
          menu.title.toLowerCase().includes(query)
        )
      })).filter(folder => folder.menus.length > 0);
    });

    function openFolder(index) {
      openFolderIndex.value = index;
    }

    function closeFolder() {
      openFolderIndex.value = null;
    }

    function resetState() {
      openFolderIndex.value = null;
      searchQuery.value = '';
    }

    function closeDialog() {
      resetState();
      emit('close');
    }

    function onFolderOpened() {
      // 폴더 열림 완료 후 처리
    }

    function handleMenuClick(menu) {
      if (menu.action) {
        menu.action();
      }
      closeDialog();
    }

    function handleSearch() {
      if (searchQuery.value && openFolderIndex.value !== null) {
        closeFolder();
      }
    }

    function clearSearch() {
      searchQuery.value = '';
    }

    function handleKeydown(e) {
      if (e.key === 'Escape') {
        if (openFolderIndex.value !== null) {
          closeFolder();
        } else {
          closeDialog();
        }
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown);
      // 열릴 때 상태 초기화
      resetState();
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown);
    });

    return {
      mainMenus,
      openFolderIndex,
      searchQuery,
      filteredMenus,
      openFolder,
      closeFolder,
      closeDialog,
      onFolderOpened,
      handleMenuClick,
      handleSearch,
      clearSearch
    };
  }
};
</script>

<style scoped>
/* 오버레이 */
.ios-menu-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  box-sizing: border-box;
}

/* 메인 컨테이너 */
.ios-menu-container {
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

/* 검색창 */
.search-bar {
  width: 100%;
  max-width: 480px;
  height: 52px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 12px;
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.search-bar:focus-within {
  background: rgba(255, 255, 255, 0.18);
  transform: scale(1.02);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.15);
}

.search-bar i {
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
}

.search-bar input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: #fff;
  font-size: 17px;
  font-weight: 400;
}

.search-bar input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-bar .clear-btn {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

.search-bar .clear-btn:hover {
  transform: scale(1.2);
  color: rgba(255, 255, 255, 0.8);
}

/* 폴더 그리드 */
.folders-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  width: 100%;
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}

.folders-grid.folder-open {
  display: block;
}

/* 폴더 래퍼 */
.folder-wrapper {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}

.folder-wrapper.is-open {
  width: 100%;
}

/* 닫힌 폴더 */
.folder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.folder:hover {
  transform: scale(1.1);
}

.folder:active {
  transform: scale(0.92);
  transition: transform 0.15s ease;
}

.folder-preview {
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  padding: 10px;
  gap: 5px;
  box-sizing: border-box;
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.folder:hover .folder-preview {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.preview-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.folder-title {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.folder-count {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  margin-top: -8px;
}

/* 열린 폴더 */
.folder-expanded {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 16px 64px rgba(0, 0, 0, 0.3);
}

.folder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.folder-header h2 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.folder-header button {
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

.folder-header button:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.folder-header button:active {
  transform: scale(0.88);
  transition: transform 0.15s ease;
}

.folder-content {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 28px;
  max-height: 450px;
  overflow-y: auto;
}

.folder-content::-webkit-scrollbar {
  width: 8px;
}

.folder-content::-webkit-scrollbar-track {
  background: transparent;
}

.folder-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

/* 메뉴 아이템 */
.menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 16px 10px;
  border-radius: 20px;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.06);
}

.menu-item:active {
  transform: scale(0.9);
  transition: transform 0.15s ease;
}

.menu-icon {
  width: 68px;
  height: 68px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0.08));
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #fff;
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.menu-item:hover .menu-icon {
  background: linear-gradient(145deg, var(--color-brand, #007AFF), var(--color-brand-dark, #0056b3));
  transform: scale(1.1);
  box-shadow: 0 12px 32px rgba(0, 122, 255, 0.35);
  border-color: transparent;
}

.menu-title {
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  word-break: keep-all;
}

/* 하단 닫기 버튼 */
.bottom-close-btn {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
  margin-top: 12px;
}

.bottom-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.15);
}

.bottom-close-btn:active {
  transform: scale(0.88);
  transition: transform 0.15s ease;
}

/* ========== 애니메이션 ========== */

/* 폴더 숨김 */
.folder-hide-enter-active {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}

.folder-hide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
}

.folder-hide-enter-from {
  opacity: 0;
  transform: scale(0.7);
}

.folder-hide-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* 폴더 확장 */
.expand-enter-active {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}

.expand-leave-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.expand-enter-from {
  opacity: 0;
  transform: scale(0.6) translateY(20px);
}

.expand-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

/* 메뉴 아이템 순차 등장 */
.menu-item-enter-active {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  transition-delay: var(--delay);
}

.menu-item-leave-active {
  transition: all 0.25s ease;
}

.menu-item-enter-from {
  opacity: 0;
  transform: scale(0.4) translateY(30px);
}

.menu-item-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* 페이드 */
.fade-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* ========== 반응형 ========== */
@media (max-width: 768px) {
  .ios-menu-overlay {
    padding: 32px 20px;
  }

  .ios-menu-container {
    gap: 32px;
  }

  .folders-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  .folder-preview {
    width: 88px;
    height: 88px;
    border-radius: 22px;
  }

  .folder-content {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 20px;
  }

  .menu-icon {
    width: 60px;
    height: 60px;
    font-size: 26px;
    border-radius: 18px;
  }
}

@media (max-width: 480px) {
  .ios-menu-overlay {
    padding: 24px 16px;
  }

  .folders-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .folder-content {
    grid-template-columns: repeat(3, 1fr);
  }

  .search-bar {
    height: 48px;
  }
}
</style>